import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Paper,
  Card,
  CardContent,
  Chip,
  Alert,
  IconButton,
  Divider,
} from "@mui/material";
import {
  Assignment as AssignmentIcon,
  ChevronLeft as ChevronLeftIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  EmojiEvents as EmojiEventsIcon,
} from "@mui/icons-material";
import { base_url } from "../../config/config";

const ITEMS_PER_PAGE = 20;

const TemplateApplyModal = ({
  open,
  onClose,
  templates,
  selectedDateForTemplate,
  onApplyTemplate,
  getISODateString,
}) => {
  // Template selection state
  const [selectedTemplateForApply, setSelectedTemplateForApply] =
    useState(null);
  const [selectedAnrForTemplate, setSelectedAnrForTemplate] = useState("");
  const [selectedAnrData, setSelectedAnrData] = useState(null);

  // Template search and filter state
  const [templateSearchTerm, setTemplateSearchTerm] = useState("");
  const [templateFilter, setTemplateFilter] = useState("all"); // all, withANR, withoutANR
  const [processCountFilter, setProcessCountFilter] = useState("all"); // all, low, medium, high
  const [machineFilter, setMachineFilter] = useState([]); // multi-select machinename
  const [processLabelFilter, setProcessLabelFilter] = useState([]); // multi-select process labels

  // ANR loading state with pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [projects, setProjects] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalProjects, setTotalProjects] = useState(0);

  const listRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 10 }, (_, i) => currentYear - i);

  // Fetch projects with pagination
  const fetchProjects = useCallback(
    async (pageNum = 1, isNewSearch = false) => {
      if (isNewSearch) {
        setLoading(true);
      }

      try {
        const params = new URLSearchParams({
          page: pageNum.toString(),
          limit: ITEMS_PER_PAGE.toString(),
          sortField: "createdAt",
          sortOrder: "desc",
        });

        if (searchTerm.trim()) {
          params.append("search", searchTerm.trim());
        }
        if (year) {
          params.append("year", year);
        }

        const response = await axios.get(
          `${base_url}/v1/getAllProjects?${params.toString()}`
        );

        if (response.data.status === 1) {
          const fetchedProjects = response.data.data || [];
          const total = response.data.totalProjects || fetchedProjects.length;

          setTotalProjects(total);

          if (isNewSearch || pageNum === 1) {
            setProjects(fetchedProjects);
          } else {
            setProjects((prev) => [...prev, ...fetchedProjects]);
          }

          setHasMore(fetchedProjects.length === ITEMS_PER_PAGE);
          setError(null);
        } else {
          setError("Failed to retrieve projects.");
          setHasMore(false);
        }
      } catch (err) {
        console.error("Error fetching projects:", err);
        setError("Error while fetching projects.");
        setHasMore(false);
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    },
    [searchTerm, year]
  );

  // Load projects when modal opens and template is selected
  useEffect(() => {
    console.log("TemplateApplyModal opened:", templates);
    if (open && selectedTemplateForApply) {
      setInitialLoading(true);
      setPage(1);
      setProjects([]);
      setHasMore(true);
      fetchProjects(1, true);
    }
  }, [open, selectedTemplateForApply]);

  // Debounced search
  useEffect(() => {
    if (!open || !selectedTemplateForApply) return;

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setPage(1);
      setProjects([]);
      setHasMore(true);
      fetchProjects(1, true);
    }, 500);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchTerm, year]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!listRef.current || loading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;

    if (scrollTop + clientHeight >= scrollHeight - 100) {
      const nextPage = page + 1;
      setPage(nextPage);
      setLoading(true);
      fetchProjects(nextPage, false);
    }
  }, [loading, hasMore, page, fetchProjects]);

  // Attach scroll listener
  useEffect(() => {
    const listElement = listRef.current;
    if (listElement) {
      listElement.addEventListener("scroll", handleScroll);
      return () => listElement.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  // Handle ANR selection
  const handleSelectAnr = (project) => {
    setSelectedAnrForTemplate(project.ANR);
    setSelectedAnrData({
      anr: project.ANR,
      auftragsbezeichnung: project.Auftragsbezeichnung,
      maxLohnaufwand: project.MaxLohnaufwand || null,
      projectId: project._id,
    });
  };

  // Handle apply template
  const handleApply = () => {
    if (
      !selectedTemplateForApply ||
      !selectedAnrForTemplate ||
      !selectedAnrData
    ) {
      return;
    }

    onApplyTemplate({
      template: selectedTemplateForApply,
      anr: selectedAnrForTemplate,
      anrData: selectedAnrData,
    });

    handleClose();
  };

  // Reset and close
  const handleClose = () => {
    setSelectedTemplateForApply(null);
    setSelectedAnrForTemplate("");
    setSelectedAnrData(null);
    setSearchTerm("");
    setTemplateSearchTerm("");
    setTemplateFilter("all");
    setProcessCountFilter("all");
    setMachineFilter([]);
    setProcessLabelFilter([]);
    setYear(new Date().getFullYear().toString());
    setPage(1);
    setProjects([]);
    setError(null);
    onClose();
  };

  // Go back to template selection
  const handleBackToTemplates = () => {
    setSelectedTemplateForApply(null);
    setSelectedAnrForTemplate("");
    setSelectedAnrData(null);
    setSearchTerm("");
    setProjects([]);
  };

  // Extract all unique machine names from all templates
  const allMachineNames = React.useMemo(() => {
    const machineSet = new Set();
    templates.forEach((template) => {
      template.processes?.forEach((process) => {
        if (process.machinename) {
          machineSet.add(process.machinename);
        }
      });
    });
    return Array.from(machineSet).sort();
  }, [templates]);

  // Extract all unique process labels from all templates
  const allProcessLabels = React.useMemo(() => {
    const labelSet = new Set();
    templates.forEach((template) => {
      template.processes?.forEach((process) => {
        if (process.label) {
          labelSet.add(process.label);
        }
      });
    });
    return Array.from(labelSet).sort();
  }, [templates]);

  // Filter templates based on search and filters
  const filteredTemplates = templates.filter((template) => {
    // Search filter
    const searchLower = templateSearchTerm.toLowerCase();
    const matchesSearch =
      !templateSearchTerm ||
      template.name?.toLowerCase().includes(searchLower) ||
      template.anr?.toLowerCase().includes(searchLower) ||
      template.processes?.some(
        (p) =>
          p.label?.toLowerCase().includes(searchLower) ||
          p.machinename?.toLowerCase().includes(searchLower)
      );

    // ANR filter
    let matchesANRFilter = true;
    if (templateFilter === "withANR") {
      matchesANRFilter = !!template.anr;
    } else if (templateFilter === "withoutANR") {
      matchesANRFilter = !template.anr;
    }

    // Process count filter
    let matchesProcessCount = true;
    const processCount = template.processes?.length || 0;
    if (processCountFilter === "low") {
      matchesProcessCount = processCount <= 3;
    } else if (processCountFilter === "medium") {
      matchesProcessCount = processCount > 3 && processCount <= 10;
    } else if (processCountFilter === "high") {
      matchesProcessCount = processCount > 10;
    }

    // Machine filter
    let matchesMachine = true;
    if (machineFilter.length > 0) {
      matchesMachine = template.processes?.some((p) =>
        machineFilter.includes(p.machinename)
      );
    }

    // Process label filter
    let matchesLabels = true;
    if (processLabelFilter.length > 0) {
      matchesLabels = template.processes?.some((p) =>
        processLabelFilter.includes(p.label)
      );
    }

    return (
      matchesSearch &&
      matchesANRFilter &&
      matchesProcessCount &&
      matchesMachine &&
      matchesLabels
    );
  });

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: 600 },
          maxHeight: "85vh",
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 2,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: "1px solid",
            borderColor: "divider",
            background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
            color: "white",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {selectedTemplateForApply && (
              <IconButton
                onClick={handleBackToTemplates}
                sx={{ color: "white" }}
              >
                <ChevronLeftIcon />
              </IconButton>
            )}
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight="bold">
                <AssignmentIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                {selectedTemplateForApply
                  ? "Select ANR"
                  : "Select Template"}{" "}
                for{" "}
                {selectedDateForTemplate &&
                  getISODateString(selectedDateForTemplate)}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                {selectedTemplateForApply
                  ? `Select ANR for "${selectedTemplateForApply.name}" template`
                  : "Click a template below to continue"}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
          {!selectedTemplateForApply ? (
            // Template Selection
            templates.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <AssignmentIcon
                  sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                />
                <Typography variant="h6" color="text.secondary">
                  No templates available
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create a template first to use this feature
                </Typography>
              </Box>
            ) : (
              <>
                {/* Search and Filter for Templates */}
                <Box sx={{ mb: 3 }}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    size="small"
                    label="Search templates by name, ANR, process, or machine"
                    value={templateSearchTerm}
                    onChange={(e) => setTemplateSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>ANR Filter</InputLabel>
                        <Select
                          value={templateFilter}
                          label="ANR Filter"
                          onChange={(e) => setTemplateFilter(e.target.value)}
                        >
                          <MenuItem value="all">All Templates</MenuItem>
                          <MenuItem value="withANR">With ANR</MenuItem>
                          <MenuItem value="withoutANR">Without ANR</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Process Count</InputLabel>
                        <Select
                          value={processCountFilter}
                          label="Process Count"
                          onChange={(e) =>
                            setProcessCountFilter(e.target.value)
                          }
                        >
                          <MenuItem value="all">All Sizes</MenuItem>
                          <MenuItem value="low">Small (≤3 processes)</MenuItem>
                          <MenuItem value="medium">
                            Medium (4-10 processes)
                          </MenuItem>
                          <MenuItem value="high">
                            Large (more than 10 processes)
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Machine</InputLabel>
                        <Select
                          multiple
                          value={machineFilter}
                          label="Machine"
                          onChange={(e) => setMachineFilter(e.target.value)}
                          renderValue={(selected) =>
                            selected.length === 0
                              ? "All Machines"
                              : selected.join(", ")
                          }
                        >
                          <MenuItem value={"__all"} disabled>
                            Select one or more machines
                          </MenuItem>
                          {allMachineNames.map((machineName) => (
                            <MenuItem key={machineName} value={machineName}>
                              {machineName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Process Label</InputLabel>
                        <Select
                          multiple
                          value={processLabelFilter}
                          label="Process Label"
                          onChange={(e) =>
                            setProcessLabelFilter(e.target.value)
                          }
                          renderValue={(selected) =>
                            selected.length === 0
                              ? "All Labels"
                              : selected.join(", ")
                          }
                        >
                          <MenuItem value={"__all"} disabled>
                            Select one or more labels
                          </MenuItem>
                          {allProcessLabels.map((label) => (
                            <MenuItem key={label} value={label}>
                              {label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  {/* Active filter tags */}
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    {templateSearchTerm && (
                      <Chip
                        label={`Search: ${templateSearchTerm}`}
                        onDelete={() => setTemplateSearchTerm("")}
                        size="small"
                      />
                    )}
                    {templateFilter !== "all" && (
                      <Chip
                        label={`ANR: ${
                          templateFilter === "withANR" ? "With" : "Without"
                        }`}
                        onDelete={() => setTemplateFilter("all")}
                        size="small"
                      />
                    )}
                    {processCountFilter !== "all" && (
                      <Chip
                        label={`Processes: ${processCountFilter}`}
                        onDelete={() => setProcessCountFilter("all")}
                        size="small"
                      />
                    )}
                    {machineFilter.map((machine) => (
                      <Chip
                        key={`machine-${machine}`}
                        label={`Machine: ${machine}`}
                        onDelete={() =>
                          setMachineFilter((prev) =>
                            prev.filter((m) => m !== machine)
                          )
                        }
                        size="small"
                      />
                    ))}
                    {processLabelFilter.map((label) => (
                      <Chip
                        key={`label-${label}`}
                        label={`Label: ${label}`}
                        onDelete={() =>
                          setProcessLabelFilter((prev) =>
                            prev.filter((l) => l !== label)
                          )
                        }
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>

                {/* Results count */}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 2, display: "block" }}
                >
                  {filteredTemplates.length > 0
                    ? `Showing ${filteredTemplates.length} of ${templates.length} templates`
                    : "No templates match your filters"}
                </Typography>

                {/* Template Grid */}
                {filteredTemplates.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <SearchIcon
                      sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                    />
                    <Typography variant="h6" color="text.secondary">
                      No templates found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try adjusting your search or filters
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {filteredTemplates.map((template) => (
                      <Grid item xs={12} sm={6} key={template._id}>
                        <Card
                          sx={{
                            cursor: "pointer",
                            transition: "all 0.2s",
                            "&:hover": {
                              boxShadow: 6,
                              transform: "translateY(-4px)",
                            },
                          }}
                          onClick={() => setSelectedTemplateForApply(template)}
                        >
                          <CardContent>
                            <Typography
                              variant="h6"
                              fontWeight="bold"
                              gutterBottom
                            >
                              {template.name}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 1,
                              }}
                            >
                              <Chip
                                size="small"
                                label={`${
                                  template.processes?.length || 0
                                } processes`}
                                color="primary"
                              />
                              {template.anr && (
                                <Chip
                                  size="small"
                                  label={`ANR: ${template.anr}`}
                                  color="secondary"
                                />
                              )}
                            </Box>
                            {template.processes &&
                              template.processes.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Processes:
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 0.5,
                                      mt: 0.5,
                                    }}
                                  >
                                    {template.processes
                                      .slice(0, 3)
                                      .map((proc, idx) => (
                                        <Chip
                                          key={idx}
                                          size="small"
                                          label={proc.label}
                                          sx={{ fontSize: "10px" }}
                                        />
                                      ))}
                                    {template.processes.length > 3 && (
                                      <Chip
                                        size="small"
                                        label={`+${
                                          template.processes.length - 3
                                        } more`}
                                        sx={{ fontSize: "10px" }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                              )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </>
            )
          ) : (
            // ANR Selection with Infinite Scroll
            <Box>
              {/* Selected Template Info */}
              <Paper
                sx={{
                  p: 2,
                  mb: 3,
                  bgcolor: "primary.light",
                  color: "primary.contrastText",
                }}
              >
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Selected Template: {selectedTemplateForApply.name}
                </Typography>
                <Typography variant="body2">
                  {selectedTemplateForApply.processes?.length || 0} processes
                  will be added starting from{" "}
                  {selectedDateForTemplate &&
                    getISODateString(selectedDateForTemplate)}
                </Typography>
              </Paper>

              {/* Search and Filter */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={8}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    size="small"
                    label="Search ANR or Auftragsbezeichnung"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      endAdornment: loading && (
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Year</InputLabel>
                    <Select
                      value={year}
                      label="Year"
                      onChange={(e) => setYear(e.target.value)}
                    >
                      <MenuItem value="">All Years</MenuItem>
                      {yearOptions.map((y) => (
                        <MenuItem key={y} value={y.toString()}>
                          {y}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Selected ANR Display */}
              {selectedAnrData && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Selected ANR:</strong> {selectedAnrData.anr}
                    {selectedAnrData.auftragsbezeichnung &&
                      ` - ${selectedAnrData.auftragsbezeichnung}`}
                  </Typography>
                </Alert>
              )}

              {/* Results count */}
              {!initialLoading && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: "block" }}
                >
                  {totalProjects > 0
                    ? `Showing ${projects.length} of ${totalProjects} projects`
                    : "No projects found"}
                </Typography>
              )}

              {/* Projects List with Infinite Scroll */}
              <Box
                ref={listRef}
                sx={{
                  maxHeight: "250px",
                  overflowY: "auto",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                {initialLoading ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    py={4}
                  >
                    <CircularProgress size={30} />
                    <Typography sx={{ ml: 2 }}>Loading projects...</Typography>
                  </Box>
                ) : error ? (
                  <Box display="flex" justifyContent="center" py={3}>
                    <Typography color="error">{error}</Typography>
                  </Box>
                ) : projects.length === 0 ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <Typography color="text.secondary">
                      No projects found
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {projects.map((project, index) => (
                      <Box
                        key={`${project._id}-${index}`}
                        onClick={() => handleSelectAnr(project)}
                        sx={{
                          p: 2,
                          borderBottom: "1px solid",
                          borderColor: "divider",
                          cursor: "pointer",
                          bgcolor:
                            selectedAnrForTemplate === project.ANR
                              ? "primary.light"
                              : "transparent",
                          "&:hover": {
                            bgcolor:
                              selectedAnrForTemplate === project.ANR
                                ? "primary.light"
                                : "action.hover",
                          },
                          "&:last-child": {
                            borderBottom: "none",
                          },
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <AssignmentIcon
                            fontSize="small"
                            color={
                              selectedAnrForTemplate === project.ANR
                                ? "primary"
                                : "action"
                            }
                          />
                          <Box flex={1}>
                            <Typography variant="body2" fontWeight="bold">
                              {project.ANR}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                            >
                              {project.Auftragsbezeichnung || "No description"}
                            </Typography>
                          </Box>
                          {selectedAnrForTemplate === project.ANR && (
                            <CheckCircleIcon color="primary" fontSize="small" />
                          )}
                        </Box>
                      </Box>
                    ))}

                    {/* Loading indicator for infinite scroll */}
                    {loading && !initialLoading && (
                      <Box display="flex" justifyContent="center" py={2}>
                        <CircularProgress size={24} />
                      </Box>
                    )}

                    {/* End of list */}
                    {!hasMore && projects.length > 0 && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        align="center"
                        display="block"
                        sx={{ py: 2 }}
                      >
                        No more projects to load
                      </Typography>
                    )}
                  </>
                )}
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                This ANR will be assigned to all{" "}
                {selectedTemplateForApply.processes?.length || 0} processes from
                this template
              </Alert>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>

          {selectedTemplateForApply && (
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={!selectedAnrForTemplate || !selectedAnrData}
              startIcon={<CheckCircleIcon />}
            >
              Apply Template
            </Button>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default TemplateApplyModal;
